import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react'
import { OverlayTrigger } from '../OverlayTrigger'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../lib/utils'

const DropdownContext = createContext<DropdownContextType>({} as DropdownContextType)

type DropdownContextType = {
   isOpen: boolean
   onToggle: (data: boolean) => void
   disabled: boolean
}

type Props = {
   children?: ReactNode | ((props: DropdownContextType) => ReactNode)
   title?: string
   className?: string
   onClose?: Function
   disabled?: boolean
}

type MenuProps = {
   children?: ReactNode | ((props: DropdownContextType) => ReactNode)
   className?: string
}

type ToggleProps = {
   children?: ReactNode
   className?: string
   componentTag?: string
   /** Oculta el caret que Toggle agrega siempre por default (ej. un trigger icon-only sin label). */
   hideCaret?: boolean
}

const Dropdown = ({ children, title, className, onClose, disabled = false }: Props) => {
   const [isOpen, setIsOpen] = useState(false)
   const prevState = useRef<boolean | undefined>()

   useEffect(() => {
      if (prevState.current && !isOpen) {
         onClose && onClose()
      }
      prevState.current = isOpen
   }, [isOpen, onClose])

   useEffect(() => {
      if (disabled && isOpen) {
         setIsOpen(false)
      }
   }, [disabled, isOpen])

   function onToggle(data: boolean) {
      if (disabled) {
         return
      }
      setIsOpen(data)
   }

   return (
      <DropdownContext.Provider value={{ isOpen, onToggle, disabled }}>
         <div className={`relative ${className || ''}`}>
            <OverlayTrigger isOpen={isOpen} onToggle={onToggle}>
               {title && <OverlayTrigger.Trigger>{title}</OverlayTrigger.Trigger>}
               {typeof children === 'function' ? children({ isOpen, onToggle, disabled }) : children}
            </OverlayTrigger>
         </div>
      </DropdownContext.Provider>
   )
}

const Toggle = ({ children, className, componentTag, hideCaret }: ToggleProps) => {
   const { isOpen, disabled } = useContext(DropdownContext)
   return (
      <OverlayTrigger.Trigger
         className={cn(className, disabled && 'pointer-events-none opacity-50 cursor-not-allowed')}
         {...(componentTag && { componentTag })}
      >
         {children}
         {!hideCaret && (
            <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} {...(children ? { className: 'ml-2' } : null)} />
         )}
      </OverlayTrigger.Trigger>
   )
}

const Menu = ({ children, className }: MenuProps) => {
   const { isOpen, onToggle, disabled } = useContext(DropdownContext)
   if (!isOpen) {
      return null
   }
   return (
      <OverlayTrigger.Overlay
         className={cn(
            'absolute bg-white text-[#333] z-10 shadow border border-black border-opacity-20 py-2 rounded min-w-[120px]',
            className,
         )}
      >
         {typeof children === 'function' ? children({ isOpen, onToggle, disabled }) : children}
      </OverlayTrigger.Overlay>
   )
}

Dropdown.Toggle = Toggle
Dropdown.Menu = Menu

export { Dropdown }
